import React, { useState } from 'react';
import {
  CoverageStatusChip,
  coverageStatusPlain,
} from './JurisdictionDispositionBadge';
import { isPhaseNotAssessed } from '../../utils/exportAssessment';
interface AuditType {
  isExpands?: boolean;
  saveBtn?: any;
  selectedStep?: any;
  handleRowClick?: any;
  workflowData?: any;
  isSelected?: boolean;
  embedded?: boolean;
}

const ITARResults: React.FC<AuditType> = ({
  isExpands,
  handleRowClick,
  workflowData,
  isSelected,
  embedded = false,
}) => {
  const [ isExpand, setIsExpand ] = useState(isExpands || false);
  // Prefer the structured data from workflowData; fall back to agentSteps.output

  const itar = workflowData?.itar;
  const hasControlValue =
    itar?.controlled !== undefined && itar?.controlled !== null;
  // Machine-readable assessment state (resilience plan Slice 6). A not_assessed ITAR phase
  // produced no legal coverage verdict, so the card reuses the jurisdiction "Unresolved"
  // chip + its manual-review line (overriding any stale stored coverage_status), and the
  // headline states the screening is incomplete — even when no result field was written.
  const notAssessed = isPhaseNotAssessed(workflowData, 'itar');
  const hasResult = Boolean(
    hasControlValue || itar?.usml_category || itar?.reason || notAssessed,
  );
  const coverageForDisplay = notAssessed ? 'unresolved' : itar?.coverage_status || null;
  const collapsedText = notAssessed
    ? 'ITAR screening incomplete — manual review recommended'
    : !hasControlValue
    ? 'Awaiting information...'
    : String(itar.controlled);
  const handleCardClick = () => {
    if (hasResult) {
      setIsExpand((prev) => !prev);
      return;
    }

    handleRowClick?.();
  };
  const handleExpandClick = (event: React.MouseEvent<SVGSVGElement>) => {
    event.stopPropagation();
    setIsExpand((prev) => !prev);
  };

  // --- Richer ITAR detail (Claude path). Each row renders only when its field is
  // present, so older runs / the OpenAI path (fields absent) look exactly as before.
  const valueStyle: React.CSSProperties = {
    color: 'var(--Text-Main, var(--text-primary))',
    fontSize: '14px',
    fontWeight: 400,
    paddingLeft: '28px',
  };
  const rowIcon = (
    <svg xmlns='http://www.w3.org/2000/svg' width='15' height='13' viewBox='0 0 15 13' fill='none'>
      <path
        d='M7.41667 11.4167H2.88333C2.1366 11.4167 1.76323 11.4167 1.47801 11.2713C1.22713 11.1435 1.02316 10.9395 0.895325 10.6887C0.75 10.4034 0.75 10.0301 0.75 9.28333V2.88333C0.75 2.1366 0.75 1.76323 0.895325 1.47801C1.02316 1.22713 1.22713 1.02316 1.47801 0.895325C1.76323 0.75 2.1366 0.75 2.88333 0.75H3.15C4.64347 0.75 5.39021 0.75 5.96064 1.04065C6.46241 1.29631 6.87035 1.70426 7.12602 2.20603C7.41667 2.77646 7.41667 3.52319 7.41667 5.01667M7.41667 11.4167V5.01667M7.41667 11.4167H11.95C12.6967 11.4167 13.0701 11.4167 13.3553 11.2713C13.6062 11.1435 13.8102 10.9395 13.938 10.6887C14.0833 10.4034 14.0833 10.0301 14.0833 9.28333V2.88333C14.0833 2.1366 14.0833 1.76323 13.938 1.47801C13.8102 1.22713 13.6062 1.02316 13.3553 0.895325C13.0701 0.75 12.6967 0.75 11.95 0.75H11.6833C10.1899 0.75 9.44312 0.75 8.87269 1.04065C8.37093 1.29631 7.96298 1.70426 7.70732 2.20603C7.41667 2.77646 7.41667 3.52319 7.41667 5.01667'
        stroke='var(--text-classify)'
        strokeWidth='1.5'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </svg>
  );
  const infoRow = (label: string, node: React.ReactNode) => (
    <>
      <div className='export-divider' style={{ display: 'flex' }} />
      <div className='classify-res-comp-sec-col'>
        <div className='classify-res-comp-sec'>
          <div className='classify-res-comp-sec-left'>{rowIcon}</div>
          <div className='classify-res-comp-sec-txt'>{label}</div>
        </div>
        <div>
          <div style={valueStyle}>{node}</div>
        </div>
      </div>
    </>
  );
  const badge = (text: string, color: string) => (
    <span
      style={{
        marginLeft: 8,
        padding: '1px 8px',
        borderRadius: 10,
        fontSize: 11,
        fontWeight: 600,
        color: color,
        border: `1px solid ${color}`,
      }}
    >
      {text}
    </span>
  );
  const bulletList = (items: string[]) => (
    // paddingLeft:0 + list-style none so list values align at the same 28px baseline as the
    // plain-text rows above (Reason, USML category, …). The wrapper's valueStyle already
    // provides that 28px; the old ul paddingLeft/disc pushed these fields further right.
    <ul style={{ margin: 0, paddingLeft: 0, listStyleType: 'none' }}>
      {items.map((s, i) => (
        <li key={i} style={{ marginBottom: 4 }}>
          {s}
        </li>
      ))}
    </ul>
  );

  return (
    <>
      <div
        className={`classify-res-comp ${
          isSelected ? 'selected ' : ''
        }${embedded ? 'standard-workflow-itar-mirror' : ''}`.trim()}
      >
        {!embedded && (
          <div className='classify-res-comp-header' onClick={handleCardClick} style={{ cursor: 'pointer' }}>
          <div className='classify-res-comp-header-left-icon'>
            <svg
              xmlns='http://www.w3.org/2000/svg'
              width='16'
              height='16'
              viewBox='0 0 16 16'
              fill='none'
            >
              <g clipPath='url(#clip0_1_34180)'>
                <path
                  d='M5.3335 5.33325H5.34016M1.3335 3.46658L1.3335 6.44959C1.3335 6.77572 1.3335 6.93878 1.37034 7.09223C1.403 7.22827 1.45687 7.35833 1.52998 7.47763C1.61243 7.61219 1.72773 7.72749 1.95833 7.95809L7.07075 13.0705C7.86279 13.8625 8.2588 14.2586 8.71546 14.4069C9.11715 14.5375 9.54984 14.5375 9.95153 14.4069C10.4082 14.2586 10.8042 13.8625 11.5962 13.0705L13.0708 11.596C13.8628 10.804 14.2588 10.4079 14.4072 9.95128C14.5377 9.5496 14.5377 9.1169 14.4072 8.71522C14.2588 8.25856 13.8628 7.86254 13.0708 7.07051L7.95833 1.95809C7.72773 1.72749 7.61243 1.61219 7.47787 1.52973C7.35858 1.45663 7.22852 1.40275 7.09247 1.37009C6.93902 1.33325 6.77596 1.33325 6.44984 1.33325L3.46683 1.33325C2.72009 1.33325 2.34672 1.33325 2.06151 1.47858C1.81063 1.60641 1.60665 1.81038 1.47882 2.06126C1.3335 2.34648 1.3335 2.71985 1.3335 3.46658ZM5.66683 5.33325C5.66683 5.51735 5.51759 5.66658 5.3335 5.66658C5.1494 5.66658 5.00016 5.51735 5.00016 5.33325C5.00016 5.14916 5.1494 4.99992 5.3335 4.99992C5.51759 4.99992 5.66683 5.14916 5.66683 5.33325Z'
                  stroke='var(--text-classify)'
                  strokeWidth='1.5'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
              </g>
              <defs>
                <clipPath id='clip0_1_34180'>
                  <rect width='16' height='16' fill='white' />
                </clipPath>
              </defs>
            </svg>
          </div>
          <div className='classify-res-comp-header-text'>ITAR Check</div>
          <div
            className='classify-res-comp-header-left-icon'
            style={{ paddingTop: '2px' }}
          >
            {hasResult ? (
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='16'
                height='16'
                viewBox='0 0 16 16'
                fill='none'
              >
                <path
                  d='M13.3327 4L5.99935 11.3333L2.66602 8'
                  stroke='#17B26A'
                  strokeWidth='1.5'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
              </svg>
            ) : (
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='16'
                height='16'
                viewBox='0 0 16 16'
                fill='none'
              >
                <g clipPath='url(#clip0_1_34184)'>
                  <path
                    d='M8.00016 3.99992V7.99992L10.6668 9.33325M14.6668 7.99992C14.6668 11.6818 11.6821 14.6666 8.00016 14.6666C4.31826 14.6666 1.3335 11.6818 1.3335 7.99992C1.3335 4.31802 4.31826 1.33325 8.00016 1.33325C11.6821 1.33325 14.6668 4.31802 14.6668 7.99992Z'
                    stroke='#F79009'
                    strokeWidth='1.5'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </g>
                <defs>
                  <clipPath id='clip0_1_34184'>
                    <rect width='16' height='16' fill='white' />
                  </clipPath>
                </defs>
              </svg>
            )}
          </div>
          <div
            className='classify-res-comp-header-left-icon'
            style={{ paddingTop: '2px' }}
          >
            {!isExpand ? (
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='16'
                height='16'
                viewBox='0 0 16 16'
                fill='none'
                onClick={handleExpandClick}
              >
                <path
                  d='M10 12L6 8L10 4'
                  stroke='var(--text-classify)'
                  strokeWidth='1.5'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
              </svg>
            ) : (
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='16'
                height='16'
                viewBox='0 0 16 16'
                fill='none'
                onClick={handleExpandClick}
              >
                <path
                  d='M4 6L8 10L12 6'
                  stroke='var(--text-classify)'
                  strokeWidth='1.5'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
              </svg>
            )}
          </div>
          </div>
        )}

        {!isExpand ? (
          <p
            className='classification-status'
            onClick={embedded ? undefined : handleCardClick}
            style={{ cursor: embedded ? 'default' : 'pointer' }}
          >
            {collapsedText}
          </p>
        ) : (
          <>
            {/* <div className='classify-res-comp-body'>
              <div className='classify-res-comp-body-cont'>
                <div className='classify-res-comp-body-cont-sec'>
                  <div className='classify-res-comp-body-cont-sec-txt'>
                    {name}
                  </div>

                </div>
                <div className='classify-res-comp-body-cont-txt'>
                  {hts_code}
                </div>
              </div>
            </div> */}

            {/* <div className='classify-res-comp-btn-sec'>
              <div className='classify-res-comp-btn'>
                <svg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 20 20' fill='none'>
                  <path d='M11.6673 1.8913V5.33341C11.6673 5.80012 11.6673 6.03348 11.7581 6.21174C11.838 6.36854 11.9655 6.49602 12.1223 6.57592C12.3006 6.66675 12.5339 6.66675 13.0007 6.66675H16.4428M7.50065 12.5L10.0007 15M10.0007 15L12.5007 12.5M10.0007 15L10.0007 10M11.6673 1.66669H7.33398C5.93385 1.66669 5.23379 1.66669 4.69901 1.93917C4.2286 2.17885 3.84615 2.56131 3.60647 3.03171C3.33398 3.56649 3.33398 4.26656 3.33398 5.66669V14.3334C3.33398 15.7335 3.33398 16.4335 3.60647 16.9683C3.84615 17.4387 4.2286 17.8212 4.69901 18.0609C5.23379 18.3334 5.93385 18.3334 7.33398 18.3334H12.6673C14.0674 18.3334 14.7675 18.3334 15.3023 18.0609C15.7727 17.8212 16.1552 17.4387 16.3948 16.9683C16.6673 16.4335 16.6673 15.7335 16.6673 14.3334V6.66669L11.6673 1.66669Z' stroke='var(--text-classify)' strokeWidth='1.66667' strokeLinecap='round' strokeLinejoin='round'/>
                </svg>
                <div className='classify-res-comp-btn-txt'>
                Export Report
                </div>
              </div>

              <div className='classify-res-comp-btn-save' onClick={saveBtn}>
                <svg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 20 20' fill='none'>
                  <path d='M5.83333 2.5V5.33333C5.83333 5.80004 5.83333 6.0334 5.92416 6.21166C6.00406 6.36846 6.13154 6.49594 6.28834 6.57584C6.4666 6.66667 6.69996 6.66667 7.16667 6.66667H12.8333C13.3 6.66667 13.5334 6.66667 13.7117 6.57584C13.8685 6.49594 13.9959 6.36846 14.0758 6.21166C14.1667 6.0334 14.1667 5.80004 14.1667 5.33333V3.33333M14.1667 17.5V12.1667C14.1667 11.7 14.1667 11.4666 14.0758 11.2883C13.9959 11.1315 13.8685 11.0041 13.7117 10.9242C13.5334 10.8333 13.3 10.8333 12.8333 10.8333H7.16667C6.69996 10.8333 6.4666 10.8333 6.28834 10.9242C6.13154 11.0041 6.00406 11.1315 5.92416 11.2883C5.83333 11.4666 5.83333 11.7 5.83333 12.1667V17.5M17.5 7.77124V13.5C17.5 14.9001 17.5 15.6002 17.2275 16.135C16.9878 16.6054 16.6054 16.9878 16.135 17.2275C15.6002 17.5 14.9001 17.5 13.5 17.5H6.5C5.09987 17.5 4.3998 17.5 3.86502 17.2275C3.39462 16.9878 3.01217 16.6054 2.77248 16.135C2.5 15.6002 2.5 14.9001 2.5 13.5V6.5C2.5 5.09987 2.5 4.3998 2.77248 3.86502C3.01217 3.39462 3.39462 3.01217 3.86502 2.77248C4.3998 2.5 5.09987 2.5 6.5 2.5H12.2288C12.6364 2.5 12.8402 2.5 13.0321 2.54605C13.2021 2.58688 13.3647 2.65422 13.5138 2.7456C13.682 2.84867 13.8261 2.9928 14.1144 3.28105L16.719 5.88562C17.0072 6.17387 17.1513 6.318 17.2544 6.48619C17.3458 6.63531 17.4131 6.79789 17.4539 6.96795C17.5 7.15976 17.5 7.36358 17.5 7.77124Z' stroke='var(--text-primary)' strokeWidth='1.66667' strokeLinecap='round' strokeLinejoin='round'/>
                </svg>
                <div className='classify-res-comp-btn-txt'>
                Save Ticket
                </div>
              </div>
            </div> */}

            <div className='classify-res-comp-sec-col'>
              <div className='classify-res-comp-sec'>
                <div className='classify-res-comp-sec-left'>
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    width='16'
                    height='16'
                    viewBox='0 0 16 16'
                    fill='none'
                  >
                    <path
                      d='M13.6666 4.85173L7.99998 7.99987M7.99998 7.99987L2.33331 4.85173M7.99998 7.99987L8 14.3332M14 10.7056V5.29419C14 5.06576 14 4.95155 13.9663 4.84968C13.9366 4.75956 13.8879 4.67684 13.8236 4.60705C13.7509 4.52816 13.651 4.47269 13.4514 4.36176L8.51802 1.62102C8.32895 1.51598 8.23442 1.46346 8.1343 1.44287C8.0457 1.42464 7.95431 1.42464 7.8657 1.44287C7.76559 1.46346 7.67105 1.51598 7.48198 1.62102L2.54865 4.36176C2.34896 4.47269 2.24912 4.52816 2.17642 4.60705C2.11211 4.67684 2.06343 4.75957 2.03366 4.84968C2 4.95155 2 5.06576 2 5.29419V10.7056C2 10.934 2 11.0482 2.03366 11.1501C2.06343 11.2402 2.11211 11.3229 2.17642 11.3927C2.24912 11.4716 2.34897 11.5271 2.54865 11.638L7.48198 14.3788C7.67105 14.4838 7.76559 14.5363 7.8657 14.5569C7.95431 14.5751 8.0457 14.5751 8.1343 14.5569C8.23442 14.5363 8.32895 14.4838 8.51802 14.3788L13.4514 11.638C13.651 11.5271 13.7509 11.4716 13.8236 11.3927C13.8879 11.3229 13.9366 11.2402 13.9663 11.1501C14 11.0482 14 10.934 14 10.7056Z'
                      stroke='var(--text-classify)'
                      strokeWidth='1.5'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    />
                    <path
                      d='M11 6.33333L5 3'
                      stroke='var(--text-classify)'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    />
                  </svg>
                </div>
                <div className='classify-res-comp-sec-txt'>
                Control Result
                </div>
              </div>
              <div>
                <h2
                  style={{
                    color: 'var(--Text-Main, var(--text-primary))',
                    fontSize: '14px',
                    fontWeight: '400',
                    paddingLeft: '28px',
                  }}
                >
                  {notAssessed ? collapsedText : itar?.controlled || 'N/A'}
                </h2>
              </div>
            </div>

            {/* v2 conditional-routing — the ITAR jurisdiction coverage verdict + a
                plain-language line on whether later review continued or stopped.
                Self-hides on the OpenAI path / older runs / flag OFF. */}
            {coverageForDisplay &&
              infoRow(
                'ITAR jurisdiction coverage',
                <>
                  <CoverageStatusChip status={coverageForDisplay} />
                  <span style={{ display: 'block', marginTop: '6px' }}>
                    {coverageStatusPlain(
                      coverageForDisplay,
                      'itar',
                      workflowData?.jurisdiction_routing,
                    )}
                  </span>
                </>
              )}

            <div className='export-divider' style={{ display: 'flex' }} />

            <div className='classify-res-comp-sec-col'>
              <div className='classify-res-comp-sec'>
                <div className='classify-res-comp-sec-left'>
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    width='15'
                    height='13'
                    viewBox='0 0 15 13'
                    fill='none'
                  >
                    <path
                      d='M7.41667 11.4167H2.88333C2.1366 11.4167 1.76323 11.4167 1.47801 11.2713C1.22713 11.1435 1.02316 10.9395 0.895325 10.6887C0.75 10.4034 0.75 10.0301 0.75 9.28333V2.88333C0.75 2.1366 0.75 1.76323 0.895325 1.47801C1.02316 1.22713 1.22713 1.02316 1.47801 0.895325C1.76323 0.75 2.1366 0.75 2.88333 0.75H3.15C4.64347 0.75 5.39021 0.75 5.96064 1.04065C6.46241 1.29631 6.87035 1.70426 7.12602 2.20603C7.41667 2.77646 7.41667 3.52319 7.41667 5.01667M7.41667 11.4167V5.01667M7.41667 11.4167H11.95C12.6967 11.4167 13.0701 11.4167 13.3553 11.2713C13.6062 11.1435 13.8102 10.9395 13.938 10.6887C14.0833 10.4034 14.0833 10.0301 14.0833 9.28333V2.88333C14.0833 2.1366 14.0833 1.76323 13.938 1.47801C13.8102 1.22713 13.6062 1.02316 13.3553 0.895325C13.0701 0.75 12.6967 0.75 11.95 0.75H11.6833C10.1899 0.75 9.44312 0.75 8.87269 1.04065C8.37093 1.29631 7.96298 1.70426 7.70732 2.20603C7.41667 2.77646 7.41667 3.52319 7.41667 5.01667'
                      stroke='var(--text-classify)'
                      strokeWidth='1.5'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    />
                  </svg>
                </div>
                <div className='classify-res-comp-sec-txt'>
                USML category
                </div>
              </div>
              <div>
                <h2
                  style={{
                    color: 'var(--Text-Main, var(--text-primary))',
                    fontSize: '14px',
                    fontWeight: '400',
                    paddingLeft: '28px',
                  }}
                >
                  {itar?.usml_category || 'N/A'}
                </h2>
                {itar?.usml_paragraph ? (
                  <div
                    style={{
                      color: 'var(--Text-Main, var(--text-primary))',
                      fontSize: '14px',
                      fontWeight: '500',
                      paddingLeft: '28px',
                      marginTop: '2px',
                    }}
                  >
                    Paragraph {String(itar.usml_paragraph)}
                    {itar?.sme ? badge('SME', 'var(--low-color, #dc2626)') : null}
                    {itar?.mt_controlled ? badge('MT', 'var(--mid-color, #f59e0b)') : null}
                  </div>
                ) : null}
              </div>
            </div>

            <div className='export-divider' style={{ display: 'flex' }} />

            <div className='classify-res-comp-sec-col'>
              <div className='classify-res-comp-sec'>
                <div className='classify-res-comp-sec-left'>
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    width='15'
                    height='13'
                    viewBox='0 0 15 13'
                    fill='none'
                  >
                    <path
                      d='M7.41667 11.4167H2.88333C2.1366 11.4167 1.76323 11.4167 1.47801 11.2713C1.22713 11.1435 1.02316 10.9395 0.895325 10.6887C0.75 10.4034 0.75 10.0301 0.75 9.28333V2.88333C0.75 2.1366 0.75 1.76323 0.895325 1.47801C1.02316 1.22713 1.22713 1.02316 1.47801 0.895325C1.76323 0.75 2.1366 0.75 2.88333 0.75H3.15C4.64347 0.75 5.39021 0.75 5.96064 1.04065C6.46241 1.29631 6.87035 1.70426 7.12602 2.20603C7.41667 2.77646 7.41667 3.52319 7.41667 5.01667M7.41667 11.4167V5.01667M7.41667 11.4167H11.95C12.6967 11.4167 13.0701 11.4167 13.3553 11.2713C13.6062 11.1435 13.8102 10.9395 13.938 10.6887C14.0833 10.4034 14.0833 10.0301 14.0833 9.28333V2.88333C14.0833 2.1366 14.0833 1.76323 13.938 1.47801C13.8102 1.22713 13.6062 1.02316 13.3553 0.895325C13.0701 0.75 12.6967 0.75 11.95 0.75H11.6833C10.1899 0.75 9.44312 0.75 8.87269 1.04065C8.37093 1.29631 7.96298 1.70426 7.70732 2.20603C7.41667 2.77646 7.41667 3.52319 7.41667 5.01667'
                      stroke='var(--text-classify)'
                      strokeWidth='1.5'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    />
                  </svg>
                </div>
                <div className='classify-res-comp-sec-txt'>
                Reason
                </div>
              </div>
              <div>
                <h2
                  style={{
                    color: 'var(--Text-Main, var(--text-primary))',
                    fontSize: '14px',
                    fontWeight: '400',
                    paddingLeft: '28px',
                  }}
                >
                  {itar?.reason || 'N/A'}
                </h2>
              </div>
            </div>

            {itar?.confidence ? infoRow('Confidence', String(itar.confidence)) : null}

            {itar?.closest_alternative_category || itar?.closest_alternative_reason
              ? infoRow(
                  'Closest Alternative Considered',
                  [itar?.closest_alternative_category, itar?.closest_alternative_reason]
                    .filter(Boolean)
                    .join(' — ')
                )
              : null}

            {itar?.country_policy_note
              ? infoRow('Destination policy', String(itar.country_policy_note))
              : null}

            {itar?.license_note ? infoRow('License note', String(itar.license_note)) : null}

            {itar?.exemption_note
              ? infoRow(
                  'Exemption outlook',
                  <span>
                    {String(itar.exemption_note)}
                    {itar?.aukus_eligible === true
                      ? badge('AUKUS §126.7 may apply', 'var(--full-bar-color, #16a34a)')
                      : itar?.aukus_eligible === false
                        ? badge('On Excluded Tech List', 'var(--mid-color, #f59e0b)')
                        : null}
                  </span>,
                )
              : itar?.aukus_eligible === true
                ? infoRow('Exemption outlook', badge('AUKUS §126.7 may apply', 'var(--full-bar-color, #16a34a)'))
                : itar?.aukus_eligible === false
                  ? infoRow('Exemption outlook', badge('On Excluded Tech List', 'var(--mid-color, #f59e0b)'))
                  : null}

            {itar?.fms_channel === true
              ? infoRow(
                  'FMS channel (§126.6)',
                  'A Foreign Military Sales transfer follows the DoD/DSCA Letter of Offer and Acceptance (LOA) rather than a standard DSP license — confirm with DDTC/DSCA.',
                )
              : null}

            {Array.isArray(itar?.matched_snippets) && itar.matched_snippets.length > 0
              ? infoRow('Matched USML text', bulletList(itar.matched_snippets))
              : null}

            {Array.isArray(itar?.review_trace) && itar.review_trace.length > 0
              ? infoRow('How this was assessed', bulletList(itar.review_trace))
              : null}

            {Array.isArray(itar?.missing_information) && itar.missing_information.length > 0
              ? infoRow('Missing information', bulletList(itar.missing_information))
              : null}

            {Array.isArray(itar?.assumptions) && itar.assumptions.length > 0
              ? infoRow('Assumptions', bulletList(itar.assumptions))
              : null}

          </>
        )}
      </div>
    </>
  );
};

export default ITARResults;
